import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import "flatpickr/dist/themes/material_blue.css";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";

import ActionEdit from "components/buttons/actionEdit";
import SaveButton from "components/buttons/save";
import { API_URL } from "helpers/app_url";
import axios from "axios";
import SafeField from "helpers/SafeField";
import EmailContentEditor from "components/Common/ContentEditor";

/* ====================================================== */

const EmailTemplates = props => {
  const dispatch = useDispatch();


  const [templates, setTemplates] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddNew, setIsAddNew] = useState(false);
  const [template, setTemplate] = useState(null);
  const [templateFilterTags, setTemplateFilterTags] = useState("");

  /* ===================== EFFECT ===================== */

  useEffect(() => {
   
    fetchAllTemplates();
    fetchAllTags();
    // eslint-disable-next-line
  }, []);

  /* ===================== TOGGLE ===================== */

  const toggleAddNew = tpl => {
    if (tpl) {
      setTemplate({
        id: tpl.id,
        templateId: tpl.templateId,
        templateTitle: tpl.templateTitle,
        templateFilterTags: tpl.templateFilterTags,
        templateCategoryId: tpl.templateCategoryId,
        templateReplacementTag: tpl.templateReplacementTag,
        templateEmailSubject: tpl.templateEmailSubject,
        templateEmailBody: tpl.templateEmailBody,
        templateSMSBody: tpl.templateSMSBody,
        templateDescription: tpl.templateDescription,
        templateKeyword: tpl.templateKeyword,
        agentId: localStorage.getItem("userId"),
        isInternal: tpl.isInternal,
        isSendEmailEnabled: tpl.isSendEmailEnabled,
        isSendSMSEnabled: tpl.isSendSMSEnabled,
        isActive: tpl.isActive,
      });
    } else {
      setTemplate(null);
    }
    setIsAddNew(prev => !prev);
  };

  /* ===================== API ===================== */

  const getHeaders = () => {
    const obj = JSON.parse(localStorage.getItem("authUser"));
    const token = obj?.resultData?.bearerToken;
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  };

  const fetchAllTags = () => {
    axios
      .get(`${API_URL}SystemNotificationTag/all`, {
        headers: getHeaders(),
        params: { agentId: localStorage.getItem("userId") },
      })
      .then(res => setTags(res.data))
      .catch(() => toast.error("Failed to load tags"));
  };

  const fetchAllTemplates = () => {
    axios
      .get(`${API_URL}SystemNotificationTemplate/allAgent`, {
        headers: getHeaders(),
        params: { AgentId: localStorage.getItem("userId") },
      })
      .then(res => {
        setTemplates(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  /* ===================== SUBMIT ===================== */

  const handleSubmit = values => {
    toast.loading("Please wait...");

    const data = {
      id: values.id,
      templateId: values.templateId,
      templateTitle: values.templateTitle,
      templateFilterTags: values.templateFilterTags,
      templateCategoryId: values.templateCategoryId,
      templateReplacementTag: values.templateReplacementTag,
      templateEmailSubject: values.templateEmailSubject,
      templateEmailBody: values.templateEmailBody,
      templateSMSBody: values.templateSMSBody,
      templateDescription: values.templateDescription,
      templateKeyword: values.templateKeyword,
      agentId: parseInt(localStorage.getItem("userId")),
      isInternal: values.isInternal,
      isSendEmailEnabled: values.isSendEmailEnabled,
      isSendSMSEnabled: values.isSendSMSEnabled,
      isActive: values.isActive,
    };

    axios
      .put(`${API_URL}SystemNotificationTemplate`, data, {
        headers: getHeaders(),
      })
      .then(res => {
        toast.dismiss();
        if (res.data.success) {
          toast.success("Email template updated successfully");
          fetchAllTemplates();
          setIsAddNew(false);
        } else {
          toast.error(res.data.validationErrors?.[0] || "Error occurred");
        }
      })
      .catch(() => {
        toast.dismiss();
        toast.error("Update failed");
      });
  };

  /* ===================== FILTER ===================== */

  const getFilteredTemplates = () => {
    if (!templateFilterTags) return templates;

    return templates.filter(tpl => {
      if (!tpl.templateFilterTags) return false;

      const tagIds = tpl.templateFilterTags
        .split(",")
        .map(t => Number(t.trim()));

      return tagIds.includes(parseInt(templateFilterTags));
    });
  };

  const getTagTitles = tagIdsString => {
    if (!tagIdsString) return [];
    const tagIds = tagIdsString.split(",").map(id => id.trim());

    return tags
      .filter(tag => tagIds.includes(String(tag.id)))
      .map(tag => tag.systemNotificationTagTitle);
  };

  /* ===================== RENDER ===================== */

  return (
    <React.Fragment>
      {loading && <div className="loading">Loading…</div>}
      <ToastContainer autoClose={2000} />

      <div className="page-content">
       

        <Container fluid>
          <Row className="mb20">
            <Col xl="12">
              <Breadcrumbs
                title={props.t("General Settings")}
                breadcrumbItem={props.t("Email Notification Templates")}
              />
            </Col>
          </Row>

          <Row>
           
            <Col xl="12">
             

              <Card>
                <CardBody>
                  <Row className="mb-4">
                    <Col sm="3">
                      <select
                        className="form-select"
                        value={templateFilterTags}
                        onChange={e => setTemplateFilterTags(e.target.value)}
                      >
                        <option value="">All Tags</option>
                        {tags.map(tag => (
                          <option key={tag.id} value={tag.id}>
                            {tag.systemNotificationTagTitle}
                          </option>
                        ))}
                      </select>
                    </Col>
                  </Row>

                  <table className="table table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th>Template Name</th>
                        <th>Tags</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredTemplates().map((tpl, index) => (
                        <tr key={index}>
                          <td>
                            {tpl.templateTitle}
                            <br />
                            <small>
                              Email:{" "}
                              {tpl.isSendEmailEnabled ? "Active" : "Inactive"} |
                              SMS: {tpl.isSendSMSEnabled ? "Active" : "Inactive"}
                            </small>
                          </td>

                          <td>
                            {getTagTitles(tpl.templateFilterTags).map(
                              (title, i) => (
                                <span
                                  key={i}
                                  className="badge bg-primary me-1"
                                >
                                  {title}
                                </span>
                              )
                            )}
                          </td>

                          <td>
                            <ActionEdit onClick={() => toggleAddNew(tpl)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* ================= OFFCANVAS ================= */}

      <Offcanvas
        isOpen={isAddNew}
        direction="end"
        toggle={() => toggleAddNew()}
        style={{ width: 600 }}
      >
        <OffcanvasHeader toggle={() => toggleAddNew()}>
          Update Email Template
        </OffcanvasHeader>

        <OffcanvasBody>
          <Formik
            enableReinitialize
            initialValues={{
              id: template?.id,
              templateId: template?.templateId,
              templateEmailSubject: template?.templateEmailSubject || "",
              templateEmailBody: template?.templateEmailBody || "",
              templateSMSBody: template?.templateSMSBody || "",
              isSendEmailEnabled: template?.isSendEmailEnabled || false,
              isSendSMSEnabled: template?.isSendSMSEnabled || false,
              isActive: template?.isActive ?? true,
            }}
            validationSchema={Yup.object({
              templateEmailSubject: Yup.string().required("Required"),
            })}
            onSubmit={handleSubmit}
          >
            {({ values, handleChange, setFieldValue }) => (
              <Form>
                <FormGroup className="mb-3 d-flex gap-4">
                  <div className="form-check">
                    <SafeField
                      type="checkbox"
                      name="isSendEmailEnabled"
                      className="form-check-input"
                    />
                    <Label className="form-check-label">Active</Label>
                  </div>

                  <div className="form-check">
                    <SafeField
                      type="checkbox"
                      name="isSendSMSEnabled"
                      className="form-check-input"
                    />
                    <Label className="form-check-label">Send SMS</Label>
                  </div>
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label>Email Subject *</Label>
                  <SafeField
                    name="templateEmailSubject"
                    className="form-control"
                    onChange={handleChange}
                  />
                  <ErrorMessage
                    name="templateEmailSubject"
                    component="div"
                    className="invalid-feedback"
                  />
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label>Email Body</Label>
                  <EmailContentEditor
                    name="templateEmailBody"
                    setFieldValue={setFieldValue}
                    initialContent={values.templateEmailBody}
                  />
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label>SMS Content</Label>
                  <SafeField
                    name="templateSMSBody"
                    as="textarea"
                    className="form-control"
                    onChange={handleChange}
                  />
                </FormGroup>

                <SaveButton>Save</SaveButton>
              </Form>
            )}
          </Formik>
        </OffcanvasBody>
      </Offcanvas>
    </React.Fragment>
  );
};

/* ====================================================== */

EmailTemplates.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(EmailTemplates);
